import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { paypalContract } from "@common/contracts/paypal.contract";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { IntegrationConnectionDto } from "@common/dto/integrationConnection/integration-connection.dto";
import { financialConnectionsQueryKey } from "queryClient";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useConnectAccount } from "@components/connectAccount/ConnectAccount.context";
import { useConnectTarget } from "@hooks/useConnectTarget";
import {
    addIntegrationConnectionInQueryData,
    entitiesAccountsQueryKey,
} from "@queries/entitiesAccounts";
import { invalidateAccountRelatedQueries } from "./account.api";
import { integrationConnectionQueryKeys } from "./integrationConnection.api";

const paypalContractClient = getBackendAPIClient(paypalContract);

export function usePayPalConnectionCreationMutation(
    onSuccess?: (connection: IntegrationConnection) => void,
) {
    const { workspaceId, isOrgConnect, refreshConnections } =
        useConnectTarget();
    const { updateConnectAccountState } = useConnectAccount();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            data: Omit<
                ServerInferRequest<
                    typeof paypalContract.createConnection
                >["body"],
                "workspaceId"
            >,
        ) => {
            if (!workspaceId && !isOrgConnect) {
                throw new Error("No active workspace");
            }

            const response = await paypalContractClient.createConnection({
                body: {
                    ...data,
                    workspaceId,
                },
            });

            return IntegrationConnectionDto.deserialize(response.body);
        },
        onMutate: () => {
            updateConnectAccountState({
                isConnecting: true,
            });
        },
        onSuccess: async (connection) => {
            addIntegrationConnectionInQueryData(queryClient, connection);
            const hasAccountsNotReadyForSync =
                connection.integrationAccounts?.some(
                    (a) => !a.financialAccount?.entityId,
                );

            if (hasAccountsNotReadyForSync) {
                updateConnectAccountState({
                    isConnecting: false,
                    connectionSaved: true,
                    integrationConnection: connection,
                });
            } else {
                updateConnectAccountState({
                    isConnecting: false,
                });
            }

            queryClient.invalidateQueries({
                queryKey: integrationConnectionQueryKeys.query(
                    workspaceId ?? "",
                ),
            });
            invalidateAccountRelatedQueries({ workspaceId });
            await refreshConnections({
                provider: "paypal",
                connectionId: String(connection.id),
            });
            onSuccess?.(connection);
        },
    });
}

export function usePayPalMigrationFromPlaidMutation(
    onSuccess?: (connection: IntegrationConnection) => void,
) {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            data: ServerInferRequest<
                typeof paypalContract.migrateFromPlaid
            >["body"],
        ) => {
            const response = await paypalContractClient.migrateFromPlaid({
                body: data,
            });

            return IntegrationConnectionDto.deserialize(response.body);
        },
        onSuccess: (connection) => {
            queryClient.invalidateQueries({
                queryKey: [entitiesAccountsQueryKey],
            });
            queryClient.invalidateQueries({
                queryKey: integrationConnectionQueryKeys.query(
                    workspaceId ?? "",
                ),
            });
            queryClient.invalidateQueries({
                queryKey: [financialConnectionsQueryKey],
            });
            invalidateAccountRelatedQueries({ workspaceId });
            onSuccess?.(connection);
        },
    });
}
